import React, { useState, MouseEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { CsvParseResult } from "@bn/api/apiv3";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";
import { useCsvUpload } from "@api/v3/hooks/useCsvUpload";
import { FileDropField, Banner, DepotSelect, LoadingButton } from "@components";
import { useDownload } from "@hooks";
import { UploadStatus } from "./UploadStatus";
import { BanksTable } from "./BanksTable/BanksTable";

type Props = {
  onCsvUpload: (parsingResult: CsvParseResult | null) => void;
  changeStep: () => void;
  errorMessage: string | undefined;
};

export const SelectFileAndFormat = ({
  onCsvUpload,
  changeStep,
  errorMessage,
}: Props) => {
  const { t } = useTranslation();
  const { depotId } = useParams();
  const theme = useTheme();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedDepotId = depotId ? Number(depotId) : undefined;
  const navigate = useNavigate();

  const [fileStatus, setFileStatus] = useState<{
    fileName: string;
    status: "success" | "fail";
    error?: string;
  } | null>(null);

  const isNextDisabled =
    !selectedDepotId ||
    fileStatus?.status !== "success" ||
    Boolean(errorMessage);

  const { mutate: downloadExample } = useDownload(
    "application/csv",
    "generic-beispiel.csv"
  );
  const { mutate: uploadCSV, isPending: isParsing } = useCsvUpload();

  const handleUploadCsv = (file: File[]) => {
    const formData = new FormData();
    formData.append("file", file[0], file[0].name);

    uploadCSV(
      {
        file: formData.get("file"),
      },
      {
        onSuccess: (data) => {
          const isFailed = data.status !== "ok" || !data.result?.length;
          setFileStatus({
            fileName: file[0].name,
            status: isFailed ? "fail" : "success",
            error: isFailed ? t("csvImport.csvUpload.error") : undefined,
          });
          onCsvUpload(data);
        },
        onError: (data) => {
          setFileStatus({
            fileName: file[0].name,
            status: "fail",
            // @ts-ignore
            error: data.response?.data?.[0].detail,
          });
        },
      }
    );
  };

  const handleDownloadExample = () => {
    downloadExample({
      url: `${import.meta.env.VITE_APP_APP_URL}/generic-beispiel.csv`,
    });
  };

  const goBack = () => {
    window.history.back();
  };

  const handleFileDelete = (event: MouseEvent) => {
    event.stopPropagation();
    setFileStatus(null);
    onCsvUpload(null);
  };

  const handleDepotChange = (depotId: number) => {
    navigate(`/depots/${depotId}/csv-drop`, {
      replace: true,
    });
  };

  return (
    <Box maxWidth={716} display="flex" flexDirection="column" gap={6} mt={6}>
      <DepotSelect
        onDepotChange={handleDepotChange}
        selectedDepotId={selectedDepotId}
      />
      <Typography color="textSecondary" variant="body3">
        <Trans
          components={{
            linkText: <Link target="_blank" onClick={handleDownloadExample} />,
          }}
          i18nKey="csvImport.csvUpload.genericHint"
        />
      </Typography>
      <BanksTable />
      <Typography color={theme.palette.error.main} variant="body3" mt={2}>
        <Trans
          components={{
            linkText: <Link target="_blank" onClick={handleDownloadExample} />,
          }}
          i18nKey="csvImport.csvUpload.currencyWarning"
        />
      </Typography>
      <FileDropField
        isParsing={isParsing}
        onDrop={handleUploadCsv}
        text={
          fileStatus ? (
            <UploadStatus
              status={fileStatus.status}
              fileName={fileStatus.fileName}
              onFileDelete={handleFileDelete}
            />
          ) : (
            t("csvImport.csvUpload.text")
          )
        }
        accept={{
          "application/xlsx": [".csv", ".xlsx"],
        }}
        maxFiles={1}
      />
      {(fileStatus?.status === "fail" || errorMessage) && (
        <Banner
          type="error"
          icon={<ErrorIcon />}
          text={fileStatus?.error || errorMessage}
        />
      )}
      <Box
        display="flex"
        flexDirection={isLessSm ? "column" : "row"}
        justifyContent="space-between"
        gap={3}
      >
        <Button variant="tertiary" onClick={goBack} fullWidth={isLessSm}>
          {t("navigation.previous")}
        </Button>
        <LoadingButton
          data-testid="next-button"
          fullWidth={isLessSm}
          disabled={isNextDisabled}
          variant="primary"
          onClick={changeStep}
          sx={{
            minWidth: 108,
          }}
        >
          {t("navigation.next")}
        </LoadingButton>
      </Box>
    </Box>
  );
};
